import { createApi } from "@reduxjs/toolkit/query/react"
import { SstReservation } from "../../../../shared/reservation"
import { SstLocker } from "../../../../shared/sst"
import {
  Sst2Locker,
  Sst2Reservation,
  SQSConfigDict,
} from "shared/sstv2Types"
import { baseQuery } from "../baseQuerySl2"

// --- V2 types ---

export type Sstv2ApiInfo = {
  sstv2ApiUrl: string
  isOnline: boolean
  pingLatency: number
  tokenStatus: string
  tokenError?: string
}

export type Sstv2TokenTestResult = {
  success: boolean
  latencyMs: number
  error?: string
}

export type Sstv2ApiTestResult = {
  success: boolean
  method: string
  endpoint: string
  latency: number
  response: unknown
  error?: string
}

// --- SST V2 DLQ viewer (read-only) ---

export type Sstv2DlqQueueRow = {
  configTag: string
  configName: string
  queueName: string
  queueUrl: string
  queueArn: string | null
  mainDepth: number | null
  dlqName: string | null
  dlqUrl: string | null
  dlqArn: string | null
  dlqDepth: number | null
}

export type Sstv2DlqMessage = {
  messageId?: string
  event: string | null
  sl1LockerId: string | null
  sl2LockerId: string | null
  receiveCount: number | null
  sentTime: string | null
  /**
   * Times the reconcile tick has re-driven this envelope (`MblsRedriveCount`).
   * `null` ⇒ never re-driven or unreadable.
   */
  redriveCount: number | null
  /**
   * The tick has GIVEN UP on this envelope. A dead-lettered migration ingest means
   * mapping items that were never written, so its work is permanently absent from
   * the flip's work set — invisible to every counter and to stalled-flip detection.
   * This is the only DLQ signal that requires an operator.
   */
  redriveExhausted: boolean
  rawBody: string
}

// --- Live state of ONE migration attempt (DEV-193) ---

/**
 * Where a mapping's reservation currently lives. PHASE-AGNOSTIC on purpose — the
 * same three buckets mean "progress" on a forward attempt and "rollback outcome"
 * on a rolled-back one, and the UI supplies the vocabulary:
 *
 *  | bucket     | forward attempt      | rolled-back attempt              |
 *  |------------|----------------------|----------------------------------|
 *  | `liveOnV2` | flipped (or adopted) | ROLLBACK INCOMPLETE — still V2   |
 *  | `liveOnV1` | not yet flipped      | restored (live, reverted to v1)   |
 *  | `absent`   | missing / failed     | archived (an adopted row)         |
 */
export type Sstv2MappingRollup = {
  total: number
  totalExact: boolean
  classified: number
  truncated: boolean
  liveOnV2: number
  liveOnV1: number
  absent: number
}

export type Sstv2FlipExecutionAttempt = {
  executionArn?: string
  /** RUNNING | SUCCEEDED | FAILED | TIMED_OUT | STOPPED */
  status?: string
  startedAt?: string
  endedAt?: string
}

export type Sstv2FlipExecutionState = {
  /** False ⇒ the flip function is not wired at all. A deployment fault, not idle. */
  configured: boolean
  flipRunning: boolean
  flipExecutionArn?: string
  rollbackRunning: boolean
  rollbackExecutionArn?: string
  /**
   * Every flip execution ever started for this attempt, newest first — read from the
   * platform's execution registry, because nothing persists a restart timestamp.
   * `DurableExecutionName` dedup replaced the DDB latch on purpose (a latch left set by
   * a dead run would block the reconciler's restart), so the history is not on the job
   * record and never will be.
   *
   * EMPTY means "could not read", not "never restarted".
   */
  flipAttempts: Sstv2FlipExecutionAttempt[]
  /** Starts after the original cutover start. */
  restartCount: number
  lastStartedAt?: string
}

export type Sstv2MigrationAttemptState = {
  tenantId: string
  migrationId: string
  flip: Sstv2FlipExecutionState
  active: Sstv2MappingRollup
  /**
   * ⚠ A COUNT ONLY — and since D37 that is a GAP, not a design choice.
   *
   * This shape was correct under D5, when history was never flipped and there was
   * nothing to classify. D37 (2026-07-31, `d9047759b`) reversed that: history rows
   * are flipped exactly like active ones, so `liveOnV2` / `liveOnV1` / `absent` are
   * now just as meaningful here as they are on `active` — the backend read path
   * simply does not compute them yet.
   *
   * The visible consequence: the attempt-state panel can tell an operator how far the
   * ACTIVE walk got but only how many history mappings EXIST, so a history walk that
   * stalled half-way is indistinguishable from one that has not started. The job
   * record's `historyProcessed` counter is the only signal for it today.
   *
   * Widening this to a full `Sstv2MappingRollup` is a backend change in
   * `apps/sl2/src/**` (out of this branch's lane) — but this type and
   * `Sstv2MigrationAttemptStatePanel` are where it lands.
   */
  history: { total: number; totalExact: boolean }
}

export const partnersApi = createApi({
  reducerPath: "partnersApi",
  baseQuery,
  tagTypes: ["Sstv2SqsConfigs", "Sstv2Dlq"],
  endpoints: (builder) => ({
    getPartnerProxy: builder.query<
      { data: SstLocker[] },
      { params?: Record<string, any>; urlFragment: string }
    >({
      query: ({ params, urlFragment }) => ({
        url: "proxy" + urlFragment,
        params,
      }),
    }),
    getSstLockers: builder.query<
      { data: { items: SstLocker[]; total: number } },
      { params: Record<string, any> | undefined }
    >({
      query: ({ params }) => ({
        url: "proxy/sirsteward",
        params: {
          ...params,
          resourceType: "lockers",
        },
      }),
    }),
    // by id
    getSstLocker: builder.query<{ data: SstLocker }, { id: string }>({
      query: ({ id }) => ({
        url: `proxy/sirsteward`,
        params: {
          resourceType: "lockers",
          resourceId: id,
        },
      }),
    }),
    getSstReservations: builder.query<
      { data: SstReservation[] },
      { params: Record<string, any> }
    >({
      query: ({ params }) => ({
        url: "proxy/sirsteward",
        params: {
          ...params,
          resourceType: "reservations",
          // "status_filters[0]": "DELIVERED",
          // "status_filters[1]": "EXPIRED",
          // "status_filters[2]": "DRAFT",
        },
      }),
    }),
    // get reservation by id
    getSstReservation: builder.query<{ data: SstReservation }, { id: string }>({
      query: ({ id }) => ({
        url: `proxy/sirsteward`,
        params: {
          resourceType: "reservations",
          resourceId: id,
        },
      }),
    }),
    // get webhooks
    getSstWebhooks: builder.query<{ data: any }, unknown>({
      query: ({ params }) => ({
        url: "proxy/sirsteward",
        params: {
          resourceType: "webhooks",
        },
      }),
    }),

    // get apiInfo
    getSstApiInfo: builder.query<{ data: any }, unknown>({
      query: () => ({
        url: "proxy/sirsteward",
        params: {
          resourceType: "apiInfo",
        },
      }),
    }),
    // get somum info
    getSomumInfo: builder.query<{ data: any }, unknown>({
      query: () => ({
        url: "proxy/somum",
        params: {
          resourceType: "info",
        },
      }),
    }),

    // --- SST V2 endpoints ---

    getSstv2ApiInfo: builder.query<{ data: Sstv2ApiInfo }, void>({
      query: () => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "apiInfo",
        },
      }),
    }),
    postSstv2TokenTest: builder.query<{ data: Sstv2TokenTestResult }, void>({
      query: () => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "tokenTest",
        },
      }),
    }),
    getSstv2Lockers: builder.query<
      { data: Sst2Locker[] },
      { page?: number; count?: number }
    >({
      query: ({ page = 1, count = 20 }) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "lockers",
          page,
          count,
        },
      }),
    }),
    getSstv2Locker: builder.query<
      { data: Sst2Locker },
      { resourceId: string }
    >({
      query: ({ resourceId }) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "lockers",
          resourceId,
        },
      }),
    }),
    getSstv2Reservations: builder.query<
      { data: Sst2Reservation[] },
      { page?: number; count?: number }
    >({
      query: ({ page = 1, count = 20 }) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "reservations",
          page,
          count,
        },
      }),
    }),
    getSstv2Reservation: builder.query<
      { data: Sst2Reservation },
      { resourceId: string }
    >({
      query: ({ resourceId }) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "reservations",
          resourceId,
        },
      }),
    }),
    getSstv2SqsConfigs: builder.query<
      { data: SQSConfigDict[] },
      { page?: number; count?: number }
    >({
      query: ({ page = 1, count = 20 }) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "sqsConfigs",
          page,
          count,
        },
      }),
      providesTags: ["Sstv2SqsConfigs"],
    }),
    createSstv2SqsConfig: builder.mutation<
      { data: SQSConfigDict },
      SQSConfigDict
    >({
      query: (body) => ({
        url: "proxy/sirsteward-v2",
        method: "POST",
        params: { resourceType: "sqsConfigs" },
        body,
      }),
      invalidatesTags: ["Sstv2SqsConfigs"],
    }),
    updateSstv2SqsConfig: builder.mutation<
      { data: SQSConfigDict },
      { tag: string; data: Partial<SQSConfigDict> }
    >({
      query: ({ tag, data }) => ({
        url: "proxy/sirsteward-v2",
        method: "PATCH",
        params: { resourceType: "sqsConfigs", resourceId: tag },
        body: data,
      }),
      invalidatesTags: ["Sstv2SqsConfigs"],
    }),
    deleteSstv2SqsConfig: builder.mutation<
      { data: SQSConfigDict },
      { tag: string }
    >({
      query: ({ tag }) => ({
        url: "proxy/sirsteward-v2",
        method: "DELETE",
        params: { resourceType: "sqsConfigs", resourceId: tag },
      }),
      invalidatesTags: ["Sstv2SqsConfigs"],
    }),
    postSstv2ApiTest: builder.mutation<
      { data: Sstv2ApiTestResult },
      { method: string; endpoint: string; body?: string }
    >({
      query: ({ method, endpoint, body }) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "apiTest",
          method,
          endpoint,
          ...(body ? { body } : {}),
        },
      }),
    }),
    patchSstv2LockerSqsTag: builder.mutation<
      { data: Sst2Locker },
      { lockerId: string; sqsTag: string }
    >({
      query: ({ lockerId, sqsTag }) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "patchLockerSqsTag",
          lockerId,
          sqsTag,
        },
      }),
    }),
    getSstv2EventDiagrams: builder.query<
      {
        data: Array<{
          eventType: string
          label: string
          category: "locker" | "reservation" | "door"
          description: string
          diagram: string
        }>
      },
      void
    >({
      query: () => ({ url: "sstv2/event-diagrams" }),
    }),
    // SST V2 DLQ viewer (read-only) — direct routes on fnThingsV2, NOT the
    // partner proxy (mirrors getSstv2EventDiagrams).
    getSstv2DlqQueues: builder.query<{ data: Sstv2DlqQueueRow[] }, void>({
      query: () => ({ url: "sstv2/dlq/queues" }),
      providesTags: ["Sstv2Dlq"],
    }),
    getSstv2DlqMessages: builder.query<
      { data: Sstv2DlqMessage[] },
      { queueUrl: string }
    >({
      query: ({ queueUrl }) => ({
        url: "sstv2/dlq/messages",
        params: { queueUrl },
      }),
      providesTags: ["Sstv2Dlq"],
    }),
    // Live state of ONE migration attempt — read-only. Answers the three things
    // the job counters cannot: is a durable flip alive, how much of the mapping
    // collection is actually flipped, and restored-vs-archived after a rollback.
    // Not tagged: nothing in this app invalidates it, and it is polled instead.
    getSstv2MigrationAttemptState: builder.query<
      { data: Sstv2MigrationAttemptState },
      { tenantId: string; migrationId: string }
    >({
      query: ({ tenantId, migrationId }) => ({
        url: "sstv2/migration/attempt-state",
        params: { tenantId, migrationId },
      }),
    }),
  }),
})

export const {
  useGetPartnerProxyQuery,
  useGetSstLockerQuery,
  useGetSstLockersQuery,
  useGetSstReservationsQuery,
  useGetSstReservationQuery,
  useGetSstWebhooksQuery,
  useGetSstApiInfoQuery,
  useGetSomumInfoQuery,
  // V2 hooks
  useGetSstv2ApiInfoQuery,
  usePostSstv2TokenTestQuery,
  useLazyPostSstv2TokenTestQuery,
  useGetSstv2LockersQuery,
  useGetSstv2LockerQuery,
  useGetSstv2ReservationsQuery,
  useGetSstv2ReservationQuery,
  useGetSstv2SqsConfigsQuery,
  useCreateSstv2SqsConfigMutation,
  useUpdateSstv2SqsConfigMutation,
  useDeleteSstv2SqsConfigMutation,
  usePostSstv2ApiTestMutation,
  usePatchSstv2LockerSqsTagMutation,
  useGetSstv2EventDiagramsQuery,
  useGetSstv2DlqQueuesQuery,
  useGetSstv2DlqMessagesQuery,
  useLazyGetSstv2DlqMessagesQuery,
  useGetSstv2MigrationAttemptStateQuery,
} = partnersApi
